import * as React from 'react';
import { connect } from 'react-redux';
import { flow, each } from 'lodash';
import { Col, Modal as BootstrapModal, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { IBrandingActions, II18nMessages } from '../../services/branding/models';
import { QuoteSummary } from './scenes';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router';
import { actions as brandingActions } from '../../services/branding/reducer';
import { bindActionCreators } from 'redux';
import { IChannel, IProduct } from '../../data/api/models';
import { withJob } from 'react-jobs';
import { IQuoteActions, IQuoteState } from '../../services/quote/models';
import { actions as quoteActions } from '../../services/quote/reducer';
import Loading from '../../components/Loading/index';
import { push } from 'react-router-redux';
import { AutoAffix } from 'react-overlays';
import { FaInfoCircle } from 'react-icons/fa';
import * as Commonmark from 'react-commonmark';
import * as api from '../../data/api/api';
import axios from 'axios';

const ModalHeader = BootstrapModal.Header;
const ModalTitle = BootstrapModal.Title;
const ModalBody = BootstrapModal.Body;

interface IQuoteProps extends RouteComponentProps<any> {
  i18n: II18nMessages;
  steps: any[];
  quote: IQuoteState;
  channel: IChannel;
  actions: {
    branding: IBrandingActions,
    quote: IQuoteActions;
    push: (route: string) => void;
  };
}

interface IQuoteCompState {
  showModal: boolean;
  values: any;
}

class Quote extends React.Component<IQuoteProps, IQuoteCompState> {
  public state = {
    showModal: false,
    values: null,
    transactionId: null,
  };

  public componentWillMount() {
    const { actions, quote } = this.props;
    const product = actions.branding.getProduct();

    if (process.env.BROWSER) {
      window.addEventListener('message', this.handleWindowMessage, { passive: true });
    }

    if (! quote.initialised) {
      actions.quote.initFromProduct(product);
    }
  }

  public componentWillUnmount() {
    if (process.env.BROWSER) {
      window.removeEventListener('message', this.handleWindowMessage, { capture: false });
    }
  }

  private handleWindowMessage = (event: MessageEvent) => {
    const { actions } = this.props;

    // For Chrome, the origin property is in the event.originalEvent object.
    const origin = event.origin || event.originalEvent.origin;

    if (origin !== axios.defaults.baseURL && !api.isGlobalPayOrigin(origin)) {
      return
    }

    const data = event.data;
    if (!('policies' in data && 'status' in data)) {
      return;
    }

    this.scrollToTop();

    if (data.status !== 'complete') {
      // Show an error?

      actions.quote.error('Payment Error: ' + data.message);
      this.closeModal();

      return;
    }

    actions.quote.complete(data.policies[0]);
    this.closeModal();

    actions.quote.addStepToWizard('complete', false);

    actions.push('/complete');
  }

  public openModal = (values, transactionId) => {
    this.setState({
      showModal: true,
      transactionId,
      values,
    });
  }

  public closeModal = () => {
    this.setState({
      showModal: false,
      values: null,
    });
  }

  private getCurrentLocation = () => {
    const { location, steps } = this.props;

    let currentLocationIndex = null;

    steps.forEach((step, key) => {
      if (step.route === location.pathname) {
        currentLocationIndex = key;
      }
    });

    return currentLocationIndex;
  }

  private scrollToTop = () => {
    if (process.env.BROWSER) {
      window.scrollTo(0, 0);
    }
  }

  private handleBack = () => {
    const { steps, actions } = this.props;

    const currentLocation = this.getCurrentLocation();

    actions.push(steps[ currentLocation - 1 ].route);
    this.scrollToTop();
  }

  private handleSubmit = (formValues) => {
    const { steps, actions } = this.props;
    const currentLocation = this.getCurrentLocation();
    const nextStep = steps[ currentLocation + 1 ];

    if (nextStep.key !== 'complete') {
      formValues.steps.push(nextStep.key);

      actions.quote.addStepToWizard(nextStep.key);
    }

    if (nextStep.key === 'schemeChoice') {
      formValues.calculateAllSchemes = true;
    }

    return actions.quote.submitForm(formValues, nextStep.key === 'complete')
      .then(() => {
        if (nextStep.key === 'complete') {
          return actions.quote.purchase(formValues)
            .then((data) => {
              actions.quote.complete(data.policies[0]);
              actions.quote.addStepToWizard('complete', false);
              actions.push(nextStep.route);

              this.scrollToTop();
            })
            .catch((error) => {
              let rethrow = true;
              let message = null;

              each(error.response.data.errors, (e) => {
                if (e.id !== 'redirect-to-provider') {
                  message = e.detail;
                  return;
                }

                this.openModal(formValues, e.data.transactionId);
                rethrow = false;
              });

              if (! rethrow) {
                return;
              }

              if (message) {
                actions.quote.error(message);
              }

              throw error;
            });
        }

        actions.push(nextStep.route);
        this.scrollToTop();

        if (nextStep.calculatePremium) {
          actions.quote.calculate(formValues);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  private renderStepRoutes = (product: IProduct) => {
    const { steps, quote, i18n, channel } = this.props;

    return steps.map((step, key) => {
      const { route: routePath } = step;
      const exact = routePath === '/';

      const onBack = key > 0 ? this.handleBack : null;

      return (
        <Route key={key} exact={exact} path={routePath}>
          <step.component
            product={product}
            initialValues={quote.application}
            destinations={quote.destinations}
            channel={channel}
            i18n={i18n}
            onSubmit={this.handleSubmit}
            onBack={onBack}
            renderDefinition={this.renderDefinition(product)}
            {...this.props}
          />
        </Route>
      );
    });
  }

  private renderDefinition = (product: IProduct) => (name: string) => {
    const definition = product.metadata.definitions[name];

    if (! definition) {
      return null;
    }

    const popover = (
      <Popover
        id={`popover-${name}`}
      >
        <Commonmark source={definition} />
      </Popover>
    );

    return (
      <OverlayTrigger
        trigger="click"
        rootClose={true}
        overlay={popover}
        placement="right"
        positionLeft={200}
        positionTop={50}
      >
        <FaInfoCircle className="pointer" />
      </OverlayTrigger>
    );
  }

  private renderPaymentModal = () => {
    return (
      <BootstrapModal
        show={true}
        onHide={this.closeModal}
        bsSize="large"
        keyboard={false}
        backdrop="static"
      >
        <ModalHeader closeButton={true}>
          <ModalTitle>Payment</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <iframe allow="payment" src={api.getPaymentUrl(this.state.transactionId)} className="payments-modal" />
        </ModalBody>
      </BootstrapModal>
    );
  }

  private renderHero = () => {
    const { i18n, channel } = this.props;

    let className = 'hero-container';
    const heroMessage = 'Travel Insurance';

    if (channel.key === 'procycle-b2c') {
      className += ' hero-procycle';
    }

    return (
      <div className={className}>
        <h1>{heroMessage}</h1>
        {/*<p>{i18n.quote.hero.subTitle}</p>*/}
      </div>
    );
  }

  public render() {
    const { actions, steps, quote } = this.props;
    const product = actions.branding.getProduct();
    const currentLocation = this.getCurrentLocation() || 0;

    if (quote.application.steps && quote.application.steps.indexOf(steps[currentLocation].key) === -1) {
      return (
        <Redirect to={steps[0].route} />
      );
    }

    return (
      <div className="internal-page">
        <div className="container">
          <Route exact={true} path="/" component={this.renderHero}/>
          <Row style={{ height: '100vh' }}>
            <Col md={8}>
              <div className="quote-content-container">
                <Switch>
                  {this.renderStepRoutes(product)}
                </Switch>
              </div>
            </Col>
            <Col md={4} className="quote-summary-container">
              <AutoAffix viewportOffsetTop={15} offsetTop={15} container={this}>
                {/* This needs to have a div intermediary otherwise it doesn't work */}
                <div>
                  <QuoteSummary product={product} />
                </div>
              </AutoAffix>
            </Col>
          </Row>
        </div>

        {this.state.showModal && this.renderPaymentModal()}
      </div>
    );

  }
}

export default flow([
  withJob({
    work: (props: IQuoteProps) => {
      const promises = [];

      if (props.quote.destinations.length === 0) {
        promises.push(props.actions.quote.loadDestinations());
      }

      return Promise.all(promises);
    },
    LoadingComponent: (props) => <Loading/>,
    ErrorComponent: ({ error }) => {
      console.error(error);

      return (
        <div>
          {error.message}
        </div>
      );
    },
  }),
  connect(
    (state) => ({
      isDrawerOpened: state.header.isDrawerOpened,
      i18n: state.branding.i18n,
      quote: state.quote,
    }),
    (dispatch) => ({
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
  withRouter,
])(Quote);
